/**
 * Unseen Achievements View Component
 * Displays unseen achievements with flip/spin animation
 */

import React, { useMemo, useEffect, useState, useRef } from 'react'
import { Box } from '@mui/material'
import { SessionAchievementBadge } from '../../achievements/components/SessionAchievementBadge'
import { Achievement } from '../../achievements/types/achievementTypes'
import { SessionAchievementDisplay } from '../../achievements/types/achievementTypes'
import { BADGE_SIZE_PX, STEP_SIZE } from './achievementAnimationConstants'
import { SeenAchievementsView } from './SeenAchievementsView'
import { AchievementCardBackside } from './AchievementCardBackside'

export interface UnseenAchievementsViewProps {
  unseenAchievements: SessionAchievementDisplay[]
  allAchievements: Achievement[]
  onMarkSeen: (achievementId: string) => void
  isReportReady: boolean
  onNavigate: () => void
}

type Phase = 'revealing' | 'cycling'

// Animation constants
const FLIP_DURATION = 1200 // ms for flip animation
const SPIN_DURATION = 4000 // ms for one full rotation (slow spin)
const FADE_OUT_DURATION = 500 // ms for fade-out animation
const SPIN_DELAY = 500 // ms delay before starting fade-out after spin starts
// Total duration for one card to complete: reveal (flip) + spin delay + fade-out
const CARD_COMPLETE_DURATION = FLIP_DURATION + SPIN_DELAY + FADE_OUT_DURATION
const BATCH_SIZE = 4 // Show 4 achievements at a time

/**
 * UnseenAchievementsView - Displays unseen achievements with flip/spin animation
 */
export const UnseenAchievementsView: React.FC<UnseenAchievementsViewProps> = ({
  unseenAchievements,
  allAchievements,
  onMarkSeen
}) => {
  const [phase, setPhase] = useState<Phase>('revealing')
  const [currentBatchIndex, setCurrentBatchIndex] = useState(0)
  const [flippedAchievements, setFlippedAchievements] = useState<Set<string>>(new Set())
  const [spinningAchievements, setSpinningAchievements] = useState<Set<string>>(new Set())
  const [removingAchievements, setRemovingAchievements] = useState<Set<string>>(new Set())

  // Store all timeout IDs in a ref for proper cleanup
  const timeoutRefsRef = useRef<Map<string, NodeJS.Timeout>>(new Map())
  const containerRef = useRef<HTMLDivElement | null>(null)
  const initialPositionsRef = useRef<Record<string, { x: number; index: number }>>({})

  // CRITICAL: Capture ALL unseen achievements at mount/start - this is our source of truth for animation
  // This prevents the animation from restarting when cards are marked as seen
  const allUnseenAchievementsRef = useRef<SessionAchievementDisplay[]>([])
  const hasInitializedRef = useRef(false)

  // Initialize: capture all unseen achievements once when component mounts or when we have new achievements
  useEffect(() => {
    if (unseenAchievements.length > 0 && !hasInitializedRef.current) {
      allUnseenAchievementsRef.current = [...unseenAchievements] // Deep copy
      hasInitializedRef.current = true
    }
  }, [unseenAchievements])

  // Split captured achievements into batches of 4 - store in ref to prevent recalculation
  const achievementBatchesRef = useRef<SessionAchievementDisplay[][]>([])

  // Initialize batches once when we have achievements
  useEffect(() => {
    if (allUnseenAchievementsRef.current.length > 0 && achievementBatchesRef.current.length === 0) {
      const batches: SessionAchievementDisplay[][] = []
      const source = allUnseenAchievementsRef.current
      for (let i = 0; i < source.length; i += BATCH_SIZE) {
        batches.push(source.slice(i, i + BATCH_SIZE))
      }
      achievementBatchesRef.current = batches
    }
  }, [unseenAchievements.length])

  // Get current batch from ref - this is stable and never changes during animation
  // We'll read it directly in the useEffect to avoid dependency issues

  // Calculate positions for achievements in the row
  const achievementPositions = useMemo(() => {
    const positions: Record<string, { x: number; index: number }> = {}
    const currentBatch = achievementBatchesRef.current[currentBatchIndex] || []

    // Initialize positions for current batch
    if (currentBatch.length > 0) {
      const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1920
      const isSmallScreen = viewportWidth < 768
      const adjustedStepSize = isSmallScreen
        ? Math.min(STEP_SIZE, (viewportWidth - 100) / currentBatch.length)
        : STEP_SIZE

      currentBatch.forEach((achievement, index) => {
        const centerIndex = Math.floor(currentBatch.length / 2)
        const offsetFromCenter = (index - centerIndex) * adjustedStepSize
        initialPositionsRef.current[achievement.id] = {
          x: offsetFromCenter,
          index
        }
        positions[achievement.id] = initialPositionsRef.current[achievement.id]
      })
    }

    return positions
  }, [currentBatchIndex])

  // Calculate container width
  const containerWidth = useMemo(() => {
    const currentBatch = achievementBatchesRef.current[currentBatchIndex] || []
    if (currentBatch.length === 0 || Object.keys(achievementPositions).length === 0) {
      return '100%'
    }
    const positions = Object.values(achievementPositions)
    if (positions.length === 0) return '100%'
    const minX = Math.min(...positions.map((p) => p.x))
    const maxX = Math.max(...positions.map((p) => p.x))
    const widthNeeded = Math.abs(minX) + maxX + BADGE_SIZE_PX + 64
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1920
    const cappedWidth = Math.min(widthNeeded, viewportWidth)
    return `${cappedWidth}px`
  }, [currentBatchIndex, achievementPositions])

  // Track which batch we've already started animating
  const animatedBatchIndexRef = useRef<number>(-1)

  // Handle flip animation: flip cards one by one from left to right (current batch only)
  useEffect(() => {
    if (phase !== 'revealing') {
      return
    }

    const batchToAnimate = achievementBatchesRef.current[currentBatchIndex] || []
    if (batchToAnimate.length === 0) {
      return
    }

    // If we've already started animating this batch, don't restart
    if (animatedBatchIndexRef.current === currentBatchIndex) {
      return
    }

    // Mark this batch as animated
    animatedBatchIndexRef.current = currentBatchIndex

    // Clear any existing timeouts for new batch
    timeoutRefsRef.current.forEach((timeout) => clearTimeout(timeout))
    timeoutRefsRef.current.clear()

    // Reset animation state for new batch
    setFlippedAchievements(new Set())
    setSpinningAchievements(new Set())
    setRemovingAchievements(new Set())
    initialPositionsRef.current = {}

    // Flip each card sequentially - each card must complete before next starts
    batchToAnimate.forEach((achievement, index) => {
      const flipDelay = index * CARD_COMPLETE_DURATION
      const achievementId = achievement.id
      const flipKey = `flip-${achievementId}`
      const spinKey = `spin-${achievementId}`
      const fadeOutKey = `fadeOut-${achievementId}`
      const markSeenKey = `markSeen-${achievementId}`

      const flipTimer = setTimeout(() => {
        // Check if timer was cleared
        if (!timeoutRefsRef.current.has(flipKey)) {
          return
        }

        setFlippedAchievements((prev) => new Set(prev).add(achievementId))

        // Start spinning after flip completes
        const spinTimer = setTimeout(() => {
          if (!timeoutRefsRef.current.has(spinKey)) {
            return
          }
          setSpinningAchievements((prev) => new Set(prev).add(achievementId))

          // Start fade-out after spin delay
          const fadeOutTimer = setTimeout(() => {
            if (!timeoutRefsRef.current.has(fadeOutKey)) {
              return
            }
            setRemovingAchievements((prev) => new Set(prev).add(achievementId))

            // Mark as seen after fade-out completes
            const markSeenTimer = setTimeout(() => {
              if (!timeoutRefsRef.current.has(markSeenKey)) {
                return
              }
              onMarkSeen(achievementId)
            }, FADE_OUT_DURATION)
            timeoutRefsRef.current.set(markSeenKey, markSeenTimer)
          }, SPIN_DELAY)
          timeoutRefsRef.current.set(fadeOutKey, fadeOutTimer)
        }, FLIP_DURATION)
        timeoutRefsRef.current.set(spinKey, spinTimer)
      }, flipDelay)
      timeoutRefsRef.current.set(flipKey, flipTimer)
    })

    // After all cards in current batch are revealed and removed, move to next batch or cycling phase
    const totalDelay = batchToAnimate.length * CARD_COMPLETE_DURATION + 1000
    const batchCompleteTimer = setTimeout(() => {
      if (currentBatchIndex < achievementBatchesRef.current.length - 1) {
        setCurrentBatchIndex((prev) => prev + 1)
        animatedBatchIndexRef.current = -1 // Reset to allow next batch
      } else {
        setPhase('cycling')
      }
    }, totalDelay)
    timeoutRefsRef.current.set('batchComplete', batchCompleteTimer)

    // Cleanup function
    const currentTimeouts = timeoutRefsRef.current
    const currentBatchIndexForCleanup = currentBatchIndex
    return () => {
      // Only clear timers if we're moving to a different batch
      if (animatedBatchIndexRef.current !== currentBatchIndexForCleanup) {
        currentTimeouts.forEach((timeout) => clearTimeout(timeout))
        currentTimeouts.clear()
      }
    }
  }, [phase, currentBatchIndex, onMarkSeen]) // currentBatch is from ref, so it's stable

  // If all unseen achievements are revealed, show cycling view with all achievements
  if (phase === 'cycling') {
    return <SeenAchievementsView allAchievements={allAchievements} isReportReady={false} onNavigate={() => {}} />
  }

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 3,
        position: 'relative',
        overflow: 'visible',
        flex: 1,
        minHeight: '300px',
        perspective: '1000px',
        perspectiveOrigin: 'center center',
        '@keyframes cardSpin': {
          '0%': {
            transform: 'rotateY(0deg)'
          },
          '100%': {
            transform: 'rotateY(360deg)'
          }
        }
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          position: 'absolute',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 2,
          justifyContent: 'center',
          alignItems: 'center',
          paddingX: 2,
          width: { xs: '100vw', sm: containerWidth },
          minWidth: '100%',
          maxWidth: '100vw',
          transformStyle: 'preserve-3d',
          overflow: 'visible'
        }}
      >
        {(achievementBatchesRef.current[currentBatchIndex] || []).map((achievement: SessionAchievementDisplay) => {
          const position = achievementPositions[achievement.id]
          const isFlipped = flippedAchievements.has(achievement.id)
          const isSpinning = spinningAchievements.has(achievement.id)
          const isRemoving = removingAchievements.has(achievement.id)

          return (
            <Box
              key={achievement.id}
              sx={{
                position: 'relative',
                width: `${BADGE_SIZE_PX}px`,
                height: `${BADGE_SIZE_PX}px`,
                transform: `translate(${position?.x || 0}px, 0) scale(${isRemoving ? 0.8 : 1})`,
                transformStyle: 'preserve-3d',
                opacity: isRemoving ? 0 : 1,
                transition: isRemoving
                  ? `opacity ${FADE_OUT_DURATION}ms ease-in-out, scale ${FADE_OUT_DURATION}ms ease-in-out`
                  : 'none'
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  transformStyle: 'preserve-3d',
                  transform: isFlipped ? 'rotateY(0deg)' : 'rotateY(180deg)',
                  transition: `transform ${FLIP_DURATION}ms ease-in-out`
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    transformStyle: 'preserve-3d',
                    ...(isSpinning && {
                      animation: `cardSpin ${SPIN_DURATION}ms linear infinite`
                    })
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      top: 0,
                      left: 0,
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                      transform: 'rotateY(180deg)',
                      transformStyle: 'preserve-3d'
                    }}
                  >
                    <AchievementCardBackside size="small" />
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      top: 0,
                      left: 0,
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                      transform: 'rotateY(0deg)',
                      transformStyle: 'preserve-3d'
                    }}
                  >
                    <SessionAchievementBadge achievement={achievement} size="small" />
                  </Box>
                </Box>
              </Box>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
